import type { TemperatureProfile } from './temperature.ts';
import type { FairTimelineEvent, StormTimelineEvent, TimelineEvent } from './timeline.ts';
import type { WeakLayerSpec } from './weaklayers.ts';

/**
 * Ready-made weather histories. Each tells a typical Norwegian avalanche story; the snowpack
 * builds its layers from the weather (no hand-placed weak layers needed).
 */
export interface ScenarioPreset {
  id: string;
  name: string;
  description: string;
  events: TimelineEvent[];
  /** Weak layers placed at the start (in addition to what the weather forms). */
  weakLayers: WeakLayerSpec[];
  /** Hour to evaluate at by default (usually just after the key event). */
  evaluationHour: number;
}

const temp = (valley: number, summit: number): TemperatureProfile => ({
  lowElevation: 0,
  lowTemp: valley,
  highElevation: 1500,
  highTemp: summit,
});

let n = 0;
const id = (kind: string) => `${kind}-${++n}`;

export function storm(
  hours: number,
  newSnowCm: number,
  windSpeed: number,
  windFromDeg: number,
  t: TemperatureProfile,
): StormTimelineEvent {
  return {
    id: id('storm'),
    kind: 'storm',
    durationHours: hours,
    temperature: t,
    newSnowCm,
    referenceElevation: 500,
    orographicPctPer100m: 5,
    windSpeed,
    windFromDeg,
    inflow: 'equilibrium',
  };
}

export function fair(hours: number, clearSky: boolean, t: TemperatureProfile): FairTimelineEvent {
  return { id: id('fair'), kind: 'fair', durationHours: hours, temperature: t, clearSky };
}

export const PRESETS: ScenarioPreset[] = [
  {
    id: 'storm-on-hoar',
    name: 'Storm on surface hoar',
    description:
      'Three clear, cold nights grow surface hoar on shaded slopes; a westerly storm buries it under wind-loaded new snow. Watch the storm slabs bond over the next days while the buried hoar stays weak.',
    events: [
      fair(72, true, temp(-2, -12)),
      storm(24, 30, 12, 270, temp(2, -10)),
      fair(96, false, temp(0, -8)),
    ],
    weakLayers: [],
    evaluationHour: 96,
  },
  {
    id: 'persistent',
    name: 'Persistent weak layer',
    description:
      'A long cold, clear spell on a thin snowpack turns the surface into facets and grows hoar; two small snowfalls and a wind event build a slab on top that stays reactive for a long time.',
    events: [
      fair(168, true, temp(-6, -18)),
      storm(18, 20, 6, 225, temp(-3, -14)),
      storm(12, 0, 14, 315, temp(-5, -15)),
      storm(18, 15, 8, 270, temp(-2, -12)),
      fair(120, false, temp(-4, -12)),
    ],
    weakLayers: [],
    evaluationHour: 216,
  },
  {
    id: 'rain-on-snow',
    name: 'Rain on snow',
    description:
      'A cold storm lays down new snow, then a mild front brings rain to mid elevations: the snow gets wet and weak (wet avalanches), and freezes into a crust when it turns cold again.',
    events: [
      storm(24, 35, 10, 250, temp(0, -10)),
      storm(12, 25, 12, 230, temp(6, -2)),
      fair(48, true, temp(-4, -14)),
    ],
    weakLayers: [],
    evaluationHour: 36,
  },
  {
    id: 'spring',
    name: 'Spring melt-freeze',
    description:
      'Warm days and clear cold nights: the surface melts and refreezes into crusts each day; wet loose and wet slab avalanches in the afternoon on sunny slopes.',
    events: [
      fair(12, true, temp(10, 2)),
      fair(12, true, temp(-2, -8)),
      fair(12, true, temp(11, 3)),
      fair(12, true, temp(-1, -7)),
      fair(12, true, temp(12, 4)),
    ],
    weakLayers: [],
    evaluationHour: 60,
  },
];

export const DEFAULT_PRESET = PRESETS[0]!;
